/**
 * PriceScanManager — Global smart price scanner.
 * Two surfaces:
 *   1) "Below Cost" — products where any scheme is sold below capital.
 *      All price updates are PIN-gated (admin or TOTP only).
 *   2) "Capital Changes" — recent capital-cost moves from POs/transfers
 *      worth reviewing. ≥₱1 threshold. Skips admin-overridden ones.
 */
import { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { formatPHP } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertTriangle, RefreshCw, Check, Clock, ChevronDown, ChevronRight, X, TrendingUp, TrendingDown, Lock, Package, Printer } from 'lucide-react';
import { toast } from 'sonner';
import CalcInput from './CalcInput';
import { formatDateTime, formatTime } from '../lib/dateFormat';
import PrintEngine from '../lib/PrintEngine';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SCAN_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const SKIP_KEY      = 'agribooks_price_scan_skip';
const CAP_SNOOZE_KEY = 'agribooks_cap_snooze';

// Dialog-level skip options (below-cost scan popup, applies to whole dialog)
const SKIP_OPTIONS = [
  { label: '30 minutes', ms: 30 * 60 * 1000 },
  { label: '1 hour',     ms: 60 * 60 * 1000 },
  { label: '4 hours',    ms:  4 * 60 * 60 * 1000 },
  { label: '6 hours',    ms:  6 * 60 * 60 * 1000 },
];

// Per-alert snooze for Capital Change (capped at 24 h — capital changes are important)
const CAP_SNOOZE_OPTIONS = [
  { label: '30 minutes', ms: 30 * 60 * 1000 },
  { label: '1 hour',     ms: 60 * 60 * 1000 },
  { label: '4 hours',    ms:  4 * 60 * 60 * 1000 },
  { label: '6 hours',    ms:  6 * 60 * 60 * 1000 },
  { label: '24 hours',   ms: 24 * 60 * 60 * 1000 },
];

export default function PriceScanManager() {
  const { user, selectedBranchId, currentBranch, branches } = useAuth();
  const [dialog, setDialog] = useState(false);
  const [tab, setTab] = useState('below_cost'); // 'below_cost' | 'capital_changes'
  const [issues, setIssues] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [editPrices, setEditPrices] = useState({}); // { product_id: { scheme_key: value } }
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [showSkipMenu, setShowSkipMenu] = useState(false);
  const [skipUntil, setSkipUntil] = useState(null); // timestamp
  const [lastScanCount, setLastScanCount] = useState(null);

  // Capital-change alerts (Stage 2)
  const [capAlerts, setCapAlerts] = useState([]);
  const [capLoading, setCapLoading] = useState(false);
  const [capSchemes, setCapSchemes] = useState([]); // schemes meta echoed by backend
  const [capEditPrices, setCapEditPrices] = useState({}); // { alert_id: { scheme_key: value } }
  const [capChildEditPrices, setCapChildEditPrices] = useState({}); // { alert_id: { child_product_id: { scheme_key: value } } }
  const [capExpanded, setCapExpanded] = useState({}); // { alert_id: bool } — child-row expand state

  // Admin PIN modal — gates ALL price changes from this dialog
  const [pinModal, setPinModal] = useState(null); // { kind: 'single'|'all', issue?, onConfirm }
  const [adminPin, setAdminPin] = useState('');
  const [pinSubmitting, setPinSubmitting] = useState(false);

  // Business info for printing
  const [businessInfo, setBusinessInfo] = useState({});

  // Print prompt — shown after PIN-confirmed save (before closing the scan dialog)
  const [printPrompt, setPrintPrompt] = useState(null); // { report_type, items, schemes }

  // Per-alert snooze for Capital Changes (localStorage-backed, per alert id)
  const [capSnoozed, setCapSnoozed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CAP_SNOOZE_KEY) || '{}'); }
    catch { return {}; }
  });
  const [snoozeMenuId, setSnoozeMenuId] = useState(null); // alert id whose snooze menu is open
  const snoozeMenuRef = useRef(null);

  const scanTimerRef = useRef(null);
  const skipMenuRef = useRef(null);

  // Load skip state from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(SKIP_KEY);
    if (stored) {
      const until = parseInt(stored, 10);
      if (until > Date.now()) setSkipUntil(until);
      else localStorage.removeItem(SKIP_KEY);
    }
    // Purge any expired capital-change snoozes on mount
    try {
      const raw = JSON.parse(localStorage.getItem(CAP_SNOOZE_KEY) || '{}');
      const now = Date.now();
      const fresh = Object.fromEntries(Object.entries(raw).filter(([, v]) => v > now));
      setCapSnoozed(fresh);
      localStorage.setItem(CAP_SNOOZE_KEY, JSON.stringify(fresh));
    } catch {}
  }, []);

  // Load business info once for printing
  useEffect(() => {
    api.get(`${BACKEND_URL}/api/settings/business-info`).then(r => setBusinessInfo(r.data)).catch(() => {});
  }, []);

  // Click outside skip menu
  useEffect(() => {
    const h = (e) => { if (skipMenuRef.current && !skipMenuRef.current.contains(e.target)) setShowSkipMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Click outside capital-change snooze menu
  useEffect(() => {
    const h = (e) => { if (snoozeMenuRef.current && !snoozeMenuRef.current.contains(e.target)) setSnoozeMenuId(null); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const isInSkipWindow = () => {
    const stored = localStorage.getItem(SKIP_KEY);
    if (!stored) return false;
    const until = parseInt(stored, 10);
    if (until > Date.now()) return true;
    localStorage.removeItem(SKIP_KEY);
    return false;
  };

  // Skip the scan entirely for super admins (no tenant context — would either
  // leak cross-org data pre-fix, or pop an empty dialog post-fix). Same guard
  // for users with no organization — there's nothing to scan.
  const skipForSuperAdmin = !!user && (
    user.is_super_admin || !user.organization_id
  );

  // Smart Price Scan is a controllable feature flag (smart_price_scan). When an
  // admin / super-admin switches it OFF for the org, fully stop scanning AND
  // hide the floating pill. Features default ON unless explicitly === false.
  const smartScanDisabled = !!user && user?.subscription?.features?.smart_price_scan === false;
  const skipScan = skipForSuperAdmin || smartScanDisabled;

  const runScan = useCallback(async (showDialogIfFound = true) => {
    if (!user) return;
    if (skipScan) return; // hard guard — never scan without tenant context or when feature OFF
    // Branch is required — scan is always branch-specific (never global)
    if (!currentBranch?.id) return;
    setScanning(true);
    try {
      const branchId = currentBranch?.id || '';
      const inSkip = isInSkipWindow();
      // Always notify if issues found (even during skip, creates notification)
      const params = new URLSearchParams();
      if (branchId) params.set('branch_id', branchId);
      if (!inSkip) params.set('notify', 'true'); // only notify outside skip window

      const res = await api.get(`${BACKEND_URL}/api/products/pricing-scan?${params}`);
      const data = res.data;
      setLastScanCount(data.critical_total || 0);  // only count critical (retail/wholesale)

      if (data.total > 0) {
        setIssues(data.issues);
        setSchemes(data.schemes || []);
        // Merge new scan results into editPrices WITHOUT overwriting any
        // prices the user has already typed. The background timer fires
        // every 5 minutes — blindly replacing the whole map would wipe
        // every unsaved change the admin has entered.
        setEditPrices(prev => {
          const merged = { ...prev };
          data.issues.forEach(issue => {
            if (!merged[issue.product_id]) {
              // First time we've seen this product — initialize from server values
              merged[issue.product_id] = {};
              (data.schemes || []).forEach(s => {
                merged[issue.product_id][s.key] = issue.prices[s.key] || '';
              });
            }
            // If the product is already in `prev`, the user has touched those
            // inputs — do NOT overwrite them.
          });
          return merged;
        });

        if (showDialogIfFound && !inSkip) {
          setDialog(true);
        }
      } else {
        setIssues([]);
        setLastScanCount(0);
      }
    } catch (e) {
      // Silent fail — don't alert user on background scan errors
    }
    setScanning(false);
  }, [user, currentBranch?.id]);

  // Start scan timer
  useEffect(() => {
    if (!user) return;
    if (skipScan) return; // no scanning for super admin / no-org / feature OFF
    // Initial scan after 30 seconds (let app load first)
    const initialDelay = setTimeout(() => runScan(true), 30_000);
    // Then every 5 minutes
    scanTimerRef.current = setInterval(() => runScan(true), SCAN_INTERVAL_MS);
    return () => {
      clearTimeout(initialDelay);
      clearInterval(scanTimerRef.current);
    };
  }, [user, runScan, skipScan]);

  // Re-scan when branch changes
  useEffect(() => {
    if (user && currentBranch?.id && !skipScan) {
      runScan(false); // silent rescan on branch switch
    }
  }, [currentBranch?.id, skipScan]); // eslint-disable-line

  // ── Capital change alerts ──────────────────────────────────────────────
  const fetchCapAlerts = useCallback(async () => {
    if (!user || skipScan) return;
    // Capital change alerts are always branch-specific — never fetch org-wide
    if (!currentBranch?.id) return;
    setCapLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('branch_id', currentBranch.id);  // always required
      const r = await api.get(`${BACKEND_URL}/api/products/capital-change-alerts?${params}`);
      const alerts = r.data?.alerts || [];
      setCapAlerts(alerts);
      setCapSchemes(r.data?.schemes || []);
      // Same preservation logic as below-cost: only initialize entries
      // that aren't already in state. Never overwrite in-progress edits.
      setCapEditPrices(prev => {
        const merged = { ...prev };
        alerts.forEach(a => {
          if (!merged[a.id]) {
            merged[a.id] = {};
            (r.data?.schemes || []).forEach(s => {
              merged[a.id][s.key] = (a.prices && a.prices[s.key]) ? a.prices[s.key] : '';
            });
          }
        });
        return merged;
      });
      setCapChildEditPrices(prev => {
        const merged = { ...prev };
        alerts.forEach(a => {
          if (a.children && a.children.length && !merged[a.id]) {
            merged[a.id] = {};
            a.children.forEach(c => {
              merged[a.id][c.product_id] = {};
              (r.data?.schemes || []).forEach(s => {
                merged[a.id][c.product_id][s.key] = (c.prices && c.prices[s.key]) ? c.prices[s.key] : '';
              });
            });
          }
        });
        return merged;
      });
      setCapExpanded(prev => {
        const merged = { ...prev };
        alerts.forEach(a => {
          if (merged[a.id] === undefined) {
            merged[a.id] = !!a.has_child_warning;
          }
        });
        return merged;
      });
    } catch { /* silent */ }
    setCapLoading(false);
  }, [user, currentBranch?.id, skipScan]);

  useEffect(() => { fetchCapAlerts(); }, [fetchCapAlerts]);

  // Re-fetch when a PO or Branch Transfer is received on another page —
  // those pages fire 'agribooks:capital_changed' on window after their receive APIs succeed.
  useEffect(() => {
    const handler = () => fetchCapAlerts();
    window.addEventListener('agribooks:capital_changed', handler);
    return () => window.removeEventListener('agribooks:capital_changed', handler);
  }, [fetchCapAlerts]);

  const ackCapAlert = async (id) => {
    try {
      await api.post(`${BACKEND_URL}/api/products/capital-change-alerts/${id}/acknowledge`);
      setCapAlerts(prev => prev.filter(a => a.id !== id));
      toast.success('Alert dismissed');
    } catch (e) {
      toast.error('Failed to dismiss');
    }
  };

  const ackAllCapAlerts = async () => {
    if (user.role !== 'admin') { toast.error('Admin only'); return; }
    if (!currentBranch?.id) { toast.error('Please select a branch'); return; }
    try {
      await api.post(`${BACKEND_URL}/api/products/capital-change-alerts/acknowledge-all`, {
        branch_id: currentBranch.id,
      });
      setCapAlerts([]);
      toast.success('All alerts dismissed');
    } catch { toast.error('Failed'); }
  };

  // Per-alert snooze — hide in this session until the timer expires.
  // Backed by localStorage so it survives page refreshes within the snooze window.
  // Capped at 24 h — capital changes are important enough that longer snoozes
  // would cause meaningful margin problems to be silently ignored.
  const snoozeCapAlert = (id, ms) => {
    const until = Date.now() + ms;
    const next  = { ...capSnoozed, [id]: until };
    setCapSnoozed(next);
    localStorage.setItem(CAP_SNOOZE_KEY, JSON.stringify(next));
    setSnoozeMenuId(null);
    toast.info(`Alert snoozed — resurfaces in ${ms >= 3_600_000 ? (ms / 3_600_000) + ' hr' : '30 min'}`);
  };

  // Helper — print the price-update list immediately after PIN confirmation
  const triggerPrint = (printData) => {
    PrintEngine.print({
      type: 'smart_price_scan_report',
      data: {
        ...printData,
        branch_name:  currentBranch?.name || '',
        verified_by:  user?.full_name || user?.username || '',
      },
      format: 'dot_matrix',
      businessInfo,
    });
  };

  // Capital-change row helpers — mirrors below-cost edit/save flow but
  // posts to /smart-price-update then auto-acknowledges the alert.
  const updateCapPrice = (alertId, schemeKey, value) => {
    setCapEditPrices(prev => ({
      ...prev,
      [alertId]: { ...(prev[alertId] || {}), [schemeKey]: value },
    }));
  };

  const updateCapChildPrice = (alertId, childId, schemeKey, value) => {
    setCapChildEditPrices(prev => ({
      ...prev,
      [alertId]: {
        ...(prev[alertId] || {}),
        [childId]: { ...((prev[alertId] || {})[childId] || {}), [schemeKey]: value },
      },
    }));
  };

  const toggleCapExpanded = (alertId) => {
    setCapExpanded(prev => ({ ...prev, [alertId]: !prev[alertId] }));
  };

  const handleSaveCapAlert = (alert) => {
    // Open the same admin PIN modal — payload differentiated by kind.
    setPinModal({ kind: 'capital_change', alert });
    setAdminPin('');
  };

  const handleSkip = (skipMs) => {
    const until = Date.now() + skipMs;
    localStorage.setItem(SKIP_KEY, String(until));
    setSkipUntil(until);
    setShowSkipMenu(false);
    setDialog(false);
    // Still notify in the background
    const branchId = currentBranch?.id || '';
    const params = new URLSearchParams({ notify: 'true' });
    if (branchId) params.set('branch_id', branchId);
    api.get(`${BACKEND_URL}/api/products/pricing-scan?${params}`).catch(() => {});
    toast.info(`Price issue alert snoozed. Notification created for your records.`);
  };

  const updatePrice = (productId, schemeKey, value) => {
    setEditPrices(prev => ({
      ...prev,
      [productId]: { ...prev[productId], [schemeKey]: value },
    }));
  };

  const handleSaveProduct = (issue) => {
    // Open PIN modal — actual save happens after PIN is verified
    setPinModal({ kind: 'single', issue });
    setAdminPin('');
  };

  const handleSaveAll = () => {
    if (!issues.length) return;
    setPinModal({ kind: 'all' });
    setAdminPin('');
  };

  const handleSaveAllCap = () => {
    if (!visibleCapAlerts.length) return;
    setPinModal({ kind: 'capital_change_all' });
    setAdminPin('');
  };

  // PIN-gated execution. The backend /products/smart-price-update endpoint
  // re-validates the PIN against admin/TOTP — managers/cashiers cannot bypass.
  const executePinned = async () => {
    if (!adminPin) {
      toast.error('Admin PIN is required');
      return;
    }
    // Branch is required — all price saves go to the current branch only
    if (!currentBranch?.id) {
      toast.error('Please select a branch before saving prices');
      return;
    }
    const activeBranchId = currentBranch.id;
    setPinSubmitting(true);
    try {
      let pendingPrint = null; // populated on success, shown as print prompt after PIN closes

      if (pinModal.kind === 'single') {
        const issue = pinModal.issue;
        const prices = editPrices[issue.product_id] || {};
        const cleaned = {};
        Object.entries(prices).forEach(([k, v]) => {
          const num = parseFloat(v);
          if (!isNaN(num) && num > 0) cleaned[k] = num;
        });
        await api.post(`${BACKEND_URL}/api/products/smart-price-update`, {
          product_id: issue.product_id,
          prices: cleaned,
          branch_id: activeBranchId,
          pin: adminPin,
        });
        toast.success(`${issue.product_name} prices updated`);
        setIssues(prev => prev.filter(i => i.product_id !== issue.product_id));
        if (issues.length <= 1) {
          setDialog(false);
          toast.success('All pricing issues resolved!');
        }
        pendingPrint = {
          report_type: 'below_cost',
          items: [{
            product_name: issue.product_name,
            sku:          issue.sku,
            unit:         issue.unit || '',
            category:     issue.category || '',
            capital:      issue.effective_cost,
            lp:           issue.last_purchase,
            ma:           issue.moving_average,
            prices:       cleaned,
          }],
          schemes,
        };
      } else if (pinModal.kind === 'capital_change') {
        const alert = pinModal.alert;
        // Collect ALL parent prices with valid values — always save to branch_prices
        // even if they match global prices. Creating a branch override proves the
        // price was reviewed after the capital change.
        const parentPrices = capEditPrices[alert.id] || {};
        const parentChanges = {};
        Object.entries(parentPrices).forEach(([k, v]) => {
          const num = parseFloat(v);
          if (isNaN(num) || num <= 0) return;
          parentChanges[k] = num;
        });
        // Collect each child repack's prices (same logic — save all valid values).
        const childUpdates = []; // [{product_id, prices}]
        (alert.children || []).forEach(c => {
          const childEdits = (capChildEditPrices[alert.id] || {})[c.product_id] || {};
          const cleaned = {};
          Object.entries(childEdits).forEach(([k, v]) => {
            const num = parseFloat(v);
            if (isNaN(num) || num <= 0) return;
            cleaned[k] = num;
          });
          if (Object.keys(cleaned).length) {
            childUpdates.push({ product_id: c.product_id,
                                product_name: c.product_name,
                                prices: cleaned });
          }
        });
        if (!Object.keys(parentChanges).length && !childUpdates.length) {
          toast.error('Enter at least one price before confirming.');
          setPinSubmitting(false);
          return;
        }
        let parentApplied = false;
        const childApplied = [];
        const childFailed = [];
        // Apply parent first (so a PIN-failure short-circuits before children).
        if (Object.keys(parentChanges).length) {
          await api.post(`${BACKEND_URL}/api/products/smart-price-update`, {
            product_id: alert.product_id,
            prices: parentChanges,
            branch_id: activeBranchId,
            pin: adminPin,
          });
          parentApplied = true;
        }
        // Apply each child. Same PIN — backend re-validates per call.
        for (const cu of childUpdates) {
          try {
            await api.post(`${BACKEND_URL}/api/products/smart-price-update`, {
              product_id: cu.product_id,
              prices: cu.prices,
              branch_id: activeBranchId,
              pin: adminPin,
            });
            childApplied.push(cu.product_name);
          } catch (e) {
            childFailed.push(cu.product_name);
            // PIN error here is unrecoverable — bail.
            if (e.response?.status === 403) throw e;
          }
        }
        // Auto-acknowledge — operator reviewed AND confirmed prices.
        try {
          await api.post(
            `${BACKEND_URL}/api/products/capital-change-alerts/${alert.id}/acknowledge`
          );
        } catch { /* silent */ }
        const parts = [];
        if (parentApplied) parts.push(alert.product_name);
        if (childApplied.length) parts.push(`${childApplied.length} repack${childApplied.length === 1 ? '' : 's'}`);
        toast.success(`Branch prices confirmed: ${parts.join(' + ')}`);
        if (childFailed.length) {
          toast.error(`Failed: ${childFailed.join(', ')}`);
        }
        setCapAlerts(prev => prev.filter(a => a.id !== alert.id));
        // Trigger POS cache refresh so Sales page shows updated prices immediately
        try {
          const branchId = activeBranchId;
          if (branchId) {
            await api.get(`${BACKEND_URL}/api/sync/pos-data`, { params: { branch_id: branchId } });
          }
        } catch { /* silent — prices saved, cache refresh is best-effort */ }
        // Build print payload — parent + any children that were confirmed
        const printItems = [];
        if (parentApplied) {
          printItems.push({
            product_name: alert.product_name,
            sku:          alert.sku,
            unit:         alert.unit || '',
            category:     alert.category || '',
            old_capital:  alert.old_capital,
            new_capital:  alert.new_capital,
            source_ref:   alert.source_ref,
            source_type:  alert.source_type,
            vendor:       alert.vendor,
            changed_at:   alert.changed_at,
            lp:           alert.last_purchase,
            ma:           alert.moving_average,
            prices:       parentChanges,
          });
        }
        childUpdates.filter(cu => !childFailed.includes(cu.product_name)).forEach(cu => {
          const child = (alert.children || []).find(c => c.product_id === cu.product_id) || {};
          printItems.push({
            product_name: cu.product_name,
            sku:          child.sku || '',
            unit:         child.unit || '',
            category:     alert.category || '',
            old_capital:  child.derived_old_capital || 0,
            new_capital:  child.derived_new_capital || 0,
            source_ref:   alert.source_ref,
            source_type:  alert.source_type,
            vendor:       alert.vendor,
            changed_at:   alert.changed_at,
            lp:           0,
            ma:           0,
            prices:       cu.prices,
          });
        });
        if (printItems.length > 0) {
          pendingPrint = { report_type: 'capital_change', items: printItems, schemes: capSchemes };
        }
      } else if (pinModal.kind === 'capital_change_all') {
        // Bulk capital-change confirm — mirrors Below Cost "Fix All" pattern.
        // Loops every alert, saves parent + child prices, auto-acknowledges.
        let fixed = 0;
        let failed = 0;
        const allPrintItems = [];
        for (const alert of visibleCapAlerts) {
          const parentPrices = capEditPrices[alert.id] || {};
          const parentChanges = {};
          Object.entries(parentPrices).forEach(([k, v]) => {
            const num = parseFloat(v);
            if (isNaN(num) || num <= 0) return;
            parentChanges[k] = num;
          });
          const childUpdates = [];
          (alert.children || []).forEach(c => {
            const childEdits = (capChildEditPrices[alert.id] || {})[c.product_id] || {};
            const cleaned = {};
            Object.entries(childEdits).forEach(([k, v]) => {
              const num = parseFloat(v);
              if (isNaN(num) || num <= 0) return;
              cleaned[k] = num;
            });
            if (Object.keys(cleaned).length) {
              childUpdates.push({ product_id: c.product_id, prices: cleaned, product_name: c.product_name });
            }
          });
          if (!Object.keys(parentChanges).length && !childUpdates.length) continue;
          try {
            if (Object.keys(parentChanges).length) {
              await api.post(`${BACKEND_URL}/api/products/smart-price-update`, {
                product_id: alert.product_id,
                prices: parentChanges,
                branch_id: activeBranchId,
                pin: adminPin,
              });
              allPrintItems.push({
                product_name: alert.product_name, sku: alert.sku, unit: alert.unit || '',
                category: alert.category || '',
                old_capital: alert.old_capital, new_capital: alert.new_capital,
                source_ref: alert.source_ref, source_type: alert.source_type,
                vendor: alert.vendor, changed_at: alert.changed_at,
                lp: alert.last_purchase, ma: alert.moving_average, prices: parentChanges,
              });
            }
            for (const cu of childUpdates) {
              await api.post(`${BACKEND_URL}/api/products/smart-price-update`, {
                product_id: cu.product_id,
                prices: cu.prices,
                branch_id: activeBranchId,
                pin: adminPin,
              });
              const child = (alert.children || []).find(c => c.product_id === cu.product_id) || {};
              allPrintItems.push({
                product_name: cu.product_name, sku: child.sku || '', unit: child.unit || '',
                category: alert.category || '',
                old_capital: child.derived_old_capital || 0, new_capital: child.derived_new_capital || 0,
                source_ref: alert.source_ref, source_type: alert.source_type,
                vendor: alert.vendor, changed_at: alert.changed_at,
                lp: 0, ma: 0, prices: cu.prices,
              });
            }
            try {
              await api.post(
                `${BACKEND_URL}/api/products/capital-change-alerts/${alert.id}/acknowledge`
              );
            } catch { /* silent */ }
            fixed++;
          } catch (e) {
            failed++;
            if (e.response?.status === 403) throw e;
          }
        }
        toast.success(`${fixed} alert(s) confirmed${failed ? `, ${failed} failed` : ''}`);
        setCapAlerts([]);
        // POS cache refresh
        try {
          await api.get(`${BACKEND_URL}/api/sync/pos-data`, { params: { branch_id: activeBranchId } });
        } catch { /* silent */ }
        if (allPrintItems.length > 0) {
          pendingPrint = { report_type: 'capital_change', items: allPrintItems, schemes: capSchemes };
        }
      } else {
        // Fix All — below cost
        let fixed = 0;
        let failed = 0;
        const allPrintItems = [];
        for (const issue of issues) {
          const prices = editPrices[issue.product_id] || {};
          const cleaned = {};
          Object.entries(prices).forEach(([k, v]) => {
            const num = parseFloat(v);
            if (!isNaN(num) && num > 0) cleaned[k] = num;
          });
          if (!Object.keys(cleaned).length) continue;
          try {
            await api.post(`${BACKEND_URL}/api/products/smart-price-update`, {
              product_id: issue.product_id,
              prices: cleaned,
              branch_id: activeBranchId,
              pin: adminPin,
            });
            fixed++;
            allPrintItems.push({
              product_name: issue.product_name, sku: issue.sku, unit: issue.unit || '',
              category: issue.category || '',
              capital: issue.effective_cost, lp: issue.last_purchase, ma: issue.moving_average,
              prices: cleaned,
            });
          } catch (e) {
            failed++;
            // If PIN was wrong, abort early to avoid dozens of identical errors
            if (e.response?.status === 403) {
              throw e;
            }
          }
        }
        toast.success(`${fixed} product(s) updated${failed ? `, ${failed} failed` : ''}`);
        setDialog(false);
        setIssues([]);
        setLastScanCount(0);
        if (allPrintItems.length > 0) {
          pendingPrint = { report_type: 'below_cost', items: allPrintItems, schemes };
        }
      }
      setPinModal(null);
      setAdminPin('');
      // Show print prompt if there's anything to print
      if (pendingPrint && pendingPrint.items.length > 0) {
        setPrintPrompt(pendingPrint);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed';
      toast.error(msg);
      // Keep the modal open on PIN failure so they can retry
      if (e.response?.status !== 403) {
        setPinModal(null);
      }
    }
    setPinSubmitting(false);
  };

  // Primary schemes to show prominently
  const primarySchemes = schemes.filter(s => ['retail', 'wholesale'].includes(s.key));
  const otherSchemes = schemes.filter(s => !['retail', 'wholesale'].includes(s.key));

  // Filter out snoozed capital-change alerts so they don't show until the snooze expires
  const visibleCapAlerts = capAlerts.filter(a => {
    const until = capSnoozed[a.id];
    return !until || until <= Date.now();
  });

  if (!user) return null;
  // Feature switched off (super-admin matrix / per-org override) — render
  // nothing: no floating pill, no dialog. Scanning effects already guarded.
  if (smartScanDisabled) return null;

  return (
    <>
      {/* Floating scan indicator — shows when issues exist and dialog is closed */}
      {((lastScanCount !== null && lastScanCount > 0) || visibleCapAlerts.length > 0) && !dialog && (
        <button
          onClick={() => {
            setDialog(true);
            // Auto-open the more urgent tab
            if (lastScanCount > 0) setTab('below_cost');
            else setTab('capital_changes');
          }}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-full shadow-lg text-sm font-medium transition-all animate-bounce"
          data-testid="price-scan-alert-btn"
        >
          <AlertTriangle size={16} />
          {lastScanCount > 0 && (
            <span>{lastScanCount} below cost</span>
          )}
          {lastScanCount > 0 && visibleCapAlerts.length > 0 && <span className="opacity-50">·</span>}
          {visibleCapAlerts.length > 0 && (
            <span>{visibleCapAlerts.length} capital change{visibleCapAlerts.length === 1 ? '' : 's'}</span>
          )}
        </button>
      )}

      {/* Price Scan Dialog */}
      <Dialog open={dialog} onOpenChange={v => { if (!v) setDialog(false); }}>
        <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col p-0">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b bg-amber-50">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-600 flex items-center justify-center">
                <AlertTriangle size={18} className="text-white" />
              </div>
              <div>
                <DialogTitle style={{ fontFamily: 'Manrope' }} className="text-base font-bold text-amber-900">
                  {tab === 'capital_changes'
                    ? `Smart Price Scan — ${visibleCapAlerts.length} Capital Change${visibleCapAlerts.length !== 1 ? 's' : ''}`
                    : `Smart Price Scan — ${issues.length} Product${issues.length !== 1 ? 's' : ''} Below Cost`}
                </DialogTitle>
                <p className="text-xs text-amber-700 mt-0.5">
                  {tab === 'capital_changes' ? (
                    <>Review capital cost changes from POs and transfers. Update branch prices as needed.</>
                  ) : (
                    <><b>Retail & Wholesale</b> are below capital cost and must be fixed.
                    Other schemes (Special, Government) are optional — skip or update as needed.</>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm"
                onClick={() => runScan(true)} disabled={scanning}
                className="h-8 text-xs border-amber-300 text-amber-800 hover:bg-amber-100"
                data-testid="rescan-btn"
              >
                <RefreshCw size={12} className={`mr-1.5 ${scanning ? 'animate-spin' : ''}`} />
                Re-scan
              </Button>
              {tab === 'below_cost' && (
                <Button
                  onClick={handleSaveAll} disabled={saving || !issues.length}
                  className="h-8 text-xs bg-[#1A4D2E] hover:bg-[#14532d] text-white"
                  data-testid="fix-all-btn"
                >
                  {saving ? <RefreshCw size={12} className="animate-spin mr-1.5" /> : <Check size={12} className="mr-1.5" />}
                  Fix All
                </Button>
              )}
              {tab === 'capital_changes' && (
                <Button
                  onClick={handleSaveAllCap} disabled={saving || !visibleCapAlerts.length}
                  className="h-8 text-xs bg-[#1A4D2E] hover:bg-[#14532d] text-white"
                  data-testid="confirm-all-cap-btn"
                >
                  {saving ? <RefreshCw size={12} className="animate-spin mr-1.5" /> : <Check size={12} className="mr-1.5" />}
                  Confirm All
                </Button>
              )}
              {/* Skip button with dropdown */}
              <div className="relative" ref={skipMenuRef}>
                <Button
                  variant="outline" size="sm"
                  onClick={() => setShowSkipMenu(v => !v)}
                  className="h-8 text-xs border-slate-300 text-slate-600"
                  data-testid="skip-btn"
                >
                  <Clock size={12} className="mr-1" />
                  Skip
                  <ChevronDown size={12} className="ml-1" />
                </Button>
                {showSkipMenu && (
                  <div className="absolute right-0 top-9 z-50 bg-white border border-slate-200 rounded-lg shadow-xl w-44 py-1">
                    <p className="text-[10px] text-slate-400 px-3 py-1.5 font-medium uppercase">Snooze for...</p>
                    {SKIP_OPTIONS.map(opt => (
                      <button key={opt.ms} onClick={() => handleSkip(opt.ms)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2">
                        <Clock size={12} className="text-slate-400" />
                        {opt.label}
                      </button>
                    ))}
                    <p className="text-[9px] text-slate-400 px-3 py-1.5 border-t mt-1">
                      Notification stays in your bell even when snoozed.
                    </p>
                  </div>
                )}
              </div>
              <button onClick={() => setDialog(false)} className="text-slate-400 hover:text-slate-600 ml-1">
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Scope banner — always branch-specific */}
          {currentBranch?.id ? (
            <div className="px-6 py-2 bg-blue-50 border-b border-blue-200 flex items-center gap-2 text-[12px]"
                 data-testid="price-scope-banner-branch">
              <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
              <span className="text-blue-900">
                Prices will be updated for <strong>{currentBranch.name}</strong> only.
                Other branches are <strong>not</strong> affected.
              </span>
            </div>
          ) : (
            <div className="px-6 py-2 bg-amber-50 border-b border-amber-200 flex items-center gap-2 text-[12px]"
                 data-testid="price-scope-banner-no-branch">
              <AlertTriangle size={13} className="text-amber-600 flex-shrink-0" />
              <span className="text-amber-900">
                <strong>No branch selected.</strong> Please select a branch from the top navigation
                to run the price scan and update prices. Price changes are always branch-specific.
              </span>
            </div>
          )}
          <div className="px-6 py-2 flex items-center gap-4 text-xs border-b bg-slate-50">
            {/* Tabs */}
            <button
              onClick={() => setTab('below_cost')}
              data-testid="tab-below-cost"
              className={`pb-1.5 -mb-2 border-b-2 transition-colors text-xs font-medium ${
                tab === 'below_cost' ? 'border-amber-600 text-amber-700' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Below Cost ({issues.length})
            </button>
            <button
              onClick={() => { setTab('capital_changes'); fetchCapAlerts(); }}
              data-testid="tab-capital-changes"
              className={`pb-1.5 -mb-2 border-b-2 transition-colors text-xs font-medium ${
                tab === 'capital_changes' ? 'border-amber-600 text-amber-700' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Capital Changes ({visibleCapAlerts.length})
            </button>
            <span className="ml-auto" />
            {tab === 'below_cost' && (
              <>
                <span className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-red-100 border border-red-400" />
                  <span className="text-slate-600">Below capital — <b>must fix</b></span>
                </span>
                <span className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-amber-50 border border-amber-300" />
                  <span className="text-slate-600">Below capital — <i>optional</i></span>
                </span>
                <span className="flex items-center gap-1.5">
                  <Lock size={11} className="text-slate-500" />
                  <span className="text-slate-600">Admin PIN required</span>
                </span>
              </>
            )}
            {tab === 'capital_changes' && (
              <>
                <span className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-red-50 border border-red-500" />
                  <span className="text-slate-600"><b>Below new capital</b> — warned, not blocked</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-amber-50 border border-amber-400" />
                  <span className="text-slate-600">Thin margin &lt; 5%</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <Lock size={11} className="text-slate-500" />
                  <span className="text-slate-600">Admin PIN required</span>
                </span>
              </>
            )}
          </div>

          {/* Table */}
          {tab === 'below_cost' && (
          <div className="flex-1 overflow-auto">
            <table className="w-full text-sm border-collapse" data-testid="price-scan-table">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-100 border-b-2 border-slate-300">
                  <th className="text-left px-3 py-2.5 text-xs uppercase text-slate-600 font-semibold" style={{minWidth:'200px'}}>Product</th>
                  <th className="text-right px-3 py-2.5 text-xs uppercase text-slate-600 font-semibold" style={{minWidth:'90px'}}>Last Purchase</th>
                  <th className="text-right px-3 py-2.5 text-xs uppercase text-slate-600 font-semibold" style={{minWidth:'90px'}}>Moving Avg</th>
                  <th className="px-1 py-2.5 w-px bg-slate-200" />
                  {/* Primary: Retail + Wholesale — REQUIRED */}
                  {primarySchemes.map(s => (
                    <th key={s.key} className="text-center px-3 py-2.5 text-xs uppercase font-semibold text-[#1A4D2E] bg-emerald-50/50" style={{minWidth:'130px'}}>
                      {s.name}
                      <span className="ml-1 text-[9px] bg-red-100 text-red-700 px-1 py-0.5 rounded font-bold">Required</span>
                    </th>
                  ))}
                  {/* Other schemes — OPTIONAL */}
                  {otherSchemes.map(s => (
                    <th key={s.key} className="text-center px-3 py-2.5 text-xs uppercase font-semibold text-slate-400" style={{minWidth:'120px'}}>
                      {s.name}
                      <span className="ml-1 text-[9px] bg-slate-100 text-slate-500 px-1 py-0.5 rounded">Optional</span>
                    </th>
                  ))}
                  <th className="text-center px-3 py-2.5 text-xs uppercase text-slate-600 font-semibold w-24">Issues</th>
                  <th className="w-20 px-3 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {issues.map((issue) => {
                  const problemKeys = new Set(issue.problem_schemes.map(p => p.scheme_key));
                  return (
                    <tr key={issue.product_id} className="border-b border-slate-100 hover:bg-amber-50/20">
                      {/* Product info */}
                      <td className="px-3 py-2">
                        <p className="font-semibold text-sm text-slate-800">
                          {issue.product_name}
                          {issue.is_repack && (
                            <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 align-middle">REPACK</span>
                          )}
                        </p>
                        <p className="text-[10px] text-slate-400">
                          {issue.sku} · {issue.category}
                          {issue.is_repack && issue.parent_name && (
                            <span className="ml-1 text-purple-500"> from {issue.parent_name} ({issue.units_per_parent || 1}/parent)</span>
                          )}
                        </p>
                        <p className="text-[10px] text-amber-700 font-semibold mt-0.5">
                          Capital: {formatPHP(issue.effective_cost)}
                          {issue.is_branch_specific_cost && (
                            <span className="ml-1 text-slate-400">(branch)</span>
                          )}
                          {issue.is_repack && (
                            <span className="ml-1 text-purple-500">(derived)</span>
                          )}
                        </p>
                      </td>

                      {/* Last purchase */}
                      <td className="px-3 py-2 text-right">
                        <span className="text-sm font-medium">{formatPHP(issue.last_purchase)}</span>
                        {issue.last_purchase > 0 && issue.last_purchase !== issue.effective_cost && (
                          <p className="text-[9px] text-slate-400">LP cost</p>
                        )}
                      </td>

                      {/* Moving average */}
                      <td className="px-3 py-2 text-right">
                        <span className="text-sm font-medium">{formatPHP(issue.moving_average)}</span>
                        <p className="text-[9px] text-slate-400">Avg cost</p>
                      </td>

                      {/* Divider */}
                      <td className="px-1 bg-slate-100 w-px" />

                      {/* Primary schemes (Retail, Wholesale) — editable */}
                      {primarySchemes.map(s => {
                        const isBad = problemKeys.has(s.key);
                        const currentPrice = issue.prices[s.key] || 0;
                        const editVal = editPrices[issue.product_id]?.[s.key] ?? '';
                        const editNum = parseFloat(editVal);
                        const isFixed = !isNaN(editNum) && editNum > 0 && editNum >= issue.effective_cost;
                        return (
                          <td key={s.key} className="px-2 py-2 bg-emerald-50/30">
                            <div>
                              {/* Current price (old) */}
                              <div className={`text-[10px] mb-0.5  ${isBad ? 'text-red-500 line-through' : 'text-slate-400'}`}>
                                was: {formatPHP(currentPrice)}
                              </div>
                              {/* New price input */}
                              <CalcInput value={editVal}
 onChange={(v) => updatePrice(issue.product_id, s.key, v)}
 placeholder={`min ₱${issue.effective_cost.toFixed(2)}`}
 className={`h-8 text-sm text-right  font-bold w-full ${
 isBad && (!editNum || editNum < issue.effective_cost)
 ? 'border-red-400 bg-red-50 text-red-700'
 : isFixed
 ? 'border-emerald-400 bg-emerald-50 text-emerald-800'
 : 'border-slate-200'
 }`}
 data-testid={`price-${issue.product_id}-${s.key}`} />
                              {/* Margin indicator */}
                              {isFixed && (
                                <p className="text-[9px] text-emerald-600 mt-0.5 text-right">
                                  +{formatPHP(editNum - issue.effective_cost)} margin
                                </p>
                              )}
                              {isBad && editNum > 0 && editNum < issue.effective_cost && (
                                <p className="text-[9px] text-red-500 mt-0.5 text-right">still below cost</p>
                              )}
                            </div>
                          </td>
                        );
                      })}

                      {/* Other schemes — optional, lighter styling */}
                      {otherSchemes.map(s => {
                        const isBad = problemKeys.has(s.key);
                        const currentPrice = issue.prices[s.key] || 0;
                        const editVal = editPrices[issue.product_id]?.[s.key] ?? '';
                        const editNum = parseFloat(editVal);
                        return (
                          <td key={s.key} className="px-2 py-2 bg-slate-50/30">
                            <div>
                              <div className={`text-[10px] mb-0.5  ${isBad ? 'text-amber-500 line-through' : 'text-slate-400'}`}>
                                {formatPHP(currentPrice)}
                              </div>
                              <CalcInput value={editVal}
 onChange={(v) => updatePrice(issue.product_id, s.key, v)}
 placeholder="optional"
 className={`h-8 text-sm text-right  w-full text-slate-600 ${
 isBad && (!editNum || editNum < issue.effective_cost)
 ? 'border-amber-300 bg-amber-50/50'
 : 'border-slate-200'
 }`}
 data-testid={`price-${issue.product_id}-${s.key}`} />
                              {isBad && (
                                <p className="text-[9px] text-amber-500 mt-0.5 text-right">optional to fix</p>
                              )}
                            </div>
                          </td>
                        );
                      })}

                      {/* Issue badges — critical in red, optional in amber */}
                      <td className="px-3 py-2 text-center">
                        <div className="flex flex-col gap-0.5 items-center">
                          {issue.problem_schemes.map(p => (
                            <Badge key={p.scheme_key}
                              className={`text-[9px] px-1.5 ${p.is_critical ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                              {p.scheme_name}: -{formatPHP(p.deficit)}
                              {!p.is_critical && <span className="ml-1 opacity-60">(opt)</span>}
                            </Badge>
                          ))}
                        </div>
                      </td>

                      {/* Per-row fix button */}
                      <td className="px-2 py-2 text-center">
                        <Button size="sm" variant="outline"
                          onClick={() => handleSaveProduct(issue)}
                          className="h-7 text-[11px] text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                          data-testid={`fix-${issue.product_id}`}>
                          Fix
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}

          {/* Capital Changes tab — full editable table like Below Cost. */}
          {tab === 'capital_changes' && (
            <div className="flex-1 overflow-auto" data-testid="capital-changes-list">
              {capLoading && capAlerts.length === 0 && (
                <div className="p-12 text-center text-sm text-slate-400">Loading…</div>
              )}
              {!capLoading && visibleCapAlerts.length === 0 && (
                <div className="p-12 text-center">
                  <Check size={32} className="text-emerald-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-700">No capital changes pending review</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Retail and wholesale prices are up to date for all products, or all changes have been accepted.
                    New alerts will surface automatically when a PO or transfer changes capital costs.
                  </p>
                </div>
              )}
              {capAlerts.length > 0 && (() => {
                const capPrimary = capSchemes.filter(s => ['retail', 'wholesale'].includes(s.key));
                const capOther = capSchemes.filter(s => !['retail', 'wholesale'].includes(s.key));
                return (
                  <table className="w-full text-sm border-collapse" data-testid="capital-changes-table">
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-slate-100 border-b-2 border-slate-300">
                        <th className="text-left px-3 py-2.5 text-xs uppercase text-slate-600 font-semibold" style={{minWidth:'220px'}}>Product</th>
                        <th className="text-right px-3 py-2.5 text-xs uppercase text-slate-600 font-semibold" style={{minWidth:'90px'}}>Old Cap</th>
                        <th className="text-right px-3 py-2.5 text-xs uppercase text-slate-600 font-semibold" style={{minWidth:'150px'}}>New Cap (MA / LP)</th>
                        <th className="px-1 py-2.5 w-px bg-slate-200" />
                        {capPrimary.map(s => (
                          <th key={s.key} className="text-center px-3 py-2.5 text-xs uppercase font-semibold text-[#1A4D2E] bg-emerald-50/50" style={{minWidth:'140px'}}>
                            {s.name}
                            <span className="ml-1 text-[9px] bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded font-bold">Primary</span>
                          </th>
                        ))}
                        {capOther.map(s => (
                          <th key={s.key} className="text-center px-3 py-2.5 text-xs uppercase font-semibold text-slate-400" style={{minWidth:'120px'}}>
                            {s.name}
                            <span className="ml-1 text-[9px] bg-slate-100 text-slate-500 px-1 py-0.5 rounded">Optional</span>
                          </th>
                        ))}
                        <th className="text-center px-3 py-2.5 text-xs uppercase text-slate-600 font-semibold w-44">Source</th>
                        <th className="w-36 px-3 py-2.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleCapAlerts.map(a => {
                        const newCap = parseFloat(a.new_capital || 0);
                        const marginByKey = {};
                        (a.scheme_margins || []).forEach(sm => { marginByKey[sm.scheme_key] = sm; });

                        // Helper: format ISO date → "Jun 15" style
                        const fmtD = iso => {
                          if (!iso) return '';
                          try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
                          catch { return ''; }
                        };

                        // Plain render helper (not a component — no hooks, safe inside map)
                        const reviewChip = (label, reviewed, lastSet, updatedBy) => {
                          const d = fmtD(lastSet);
                          const capitalD = fmtD(a.changed_at);
                          const byTxt = updatedBy ? ` by ${updatedBy}` : '';
                          if (reviewed) {
                            return (
                              <span
                                key={label}
                                className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700"
                                title={`${label} updated after capital change${byTxt}${d ? ' · ' + d : ''}`}
                              >
                                <Check size={8} /> {label}{d ? ` · ${d}` : ''}{updatedBy ? ` · ${updatedBy}` : ''}
                              </span>
                            );
                          }
                          return (
                            <span
                              key={label}
                              className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700"
                              title={
                                lastSet
                                  ? `${label} last set ${d}${byTxt} — before capital changed ${capitalD}. Needs update.`
                                  : `${label} has never been explicitly set at this branch. Capital changed ${capitalD}.`
                              }
                            >
                              <AlertTriangle size={8} /> {label}{d ? ` · ${d}` : ' · not set'}
                            </span>
                          );
                        };
                        const renderPriceCell = (s, primary) => {
                          const currentPrice = parseFloat(a.prices?.[s.key] || 0);
                          const editVal = capEditPrices[a.id]?.[s.key] ?? '';
                          const editNum = parseFloat(editVal);
                          const hasEdit = !isNaN(editNum) && editNum > 0;
                          // Live warning against NEW capital — visible but never blocks save.
                          const willBeBelow = hasEdit && editNum < newCap;
                          const newMargin = hasEdit ? editNum - newCap : null;
                          const newMarginPct = hasEdit && editNum > 0 ? (newMargin / editNum * 100) : null;
                          const isThin = hasEdit && !willBeBelow && (newMargin < 5.0 || (newMarginPct !== null && newMarginPct < 5.0));
                          // Existing margin pre-edit, surfaced as context chip.
                          const sm = marginByKey[s.key];
                          return (
                            <td key={s.key} className={`px-2 py-2 ${primary ? 'bg-emerald-50/30' : 'bg-slate-50/30'}`}>
                              <div>
                                <div className={`text-[10px] mb-0.5  ${sm?.is_below_cost ? 'text-red-500 line-through' : sm?.is_thin ? 'text-amber-600' : 'text-slate-400'}`}>
                                  was: {currentPrice > 0 ? formatPHP(currentPrice) : '—'}
                                  {sm?.is_below_cost && <span className="ml-1 text-[9px] font-bold text-red-600">BELOW COST</span>}
                                  {sm?.is_thin && !sm?.is_below_cost && <span className="ml-1 text-[9px] font-bold text-amber-600">THIN</span>}
                                </div>
                                <CalcInput value={editVal}
                                  onChange={(v) => updateCapPrice(a.id, s.key, v)}
                                  placeholder={primary ? `min ₱${newCap.toFixed(2)}` : 'optional'}
                                  className={`h-8 text-sm text-right  w-full ${
                                    willBeBelow
                                      ? 'border-red-500 bg-red-50 text-red-700 font-bold ring-1 ring-red-400'
                                      : isThin
                                      ? 'border-amber-400 bg-amber-50 text-amber-800'
                                      : hasEdit
                                      ? 'border-emerald-400 bg-emerald-50 text-emerald-800 font-bold'
                                      : 'border-slate-200'
                                  }`}
                                  data-testid={`cap-price-${a.id}-${s.key}`} />
                                {/* Warning chips — visible but non-blocking. */}
                                {willBeBelow && (
                                  <p className="text-[9px] text-red-600 mt-0.5 text-right font-bold flex items-center justify-end gap-1">
                                    <AlertTriangle size={9} /> ₱{(newCap - editNum).toFixed(2)} below new capital
                                  </p>
                                )}
                                {isThin && !willBeBelow && (
                                  <p className="text-[9px] text-amber-700 mt-0.5 text-right font-semibold">
                                    Thin margin: +{formatPHP(newMargin)} ({newMarginPct?.toFixed(1)}%)
                                  </p>
                                )}
                                {hasEdit && !willBeBelow && !isThin && (
                                  <p className="text-[9px] text-emerald-600 mt-0.5 text-right">
                                    +{formatPHP(newMargin)} ({newMarginPct?.toFixed(1)}%)
                                  </p>
                                )}
                              </div>
                            </td>
                          );
                        };
                        return (
                          <Fragment key={a.id}>
                          <tr className="border-b border-slate-100 hover:bg-amber-50/20" data-testid={`cap-alert-${a.id}`}>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2 mb-0.5">
                                {a.children && a.children.length > 0 ? (
                                  <button
                                    onClick={() => toggleCapExpanded(a.id)}
                                    className="w-5 h-5 rounded hover:bg-slate-200 flex items-center justify-center text-slate-500"
                                    data-testid={`cap-expand-${a.id}`}
                                    title={`${a.children.length} repack child${a.children.length === 1 ? '' : 'ren'}`}
                                  >
                                    {capExpanded[a.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                  </button>
                                ) : null}
                                <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${a.direction === 'up' ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}`}>
                                  {a.direction === 'up' ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                                </div>
                                <p className="font-semibold text-sm text-slate-800 truncate">
                                  {a.product_name}
                                  {a.is_repack && (
                                    <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 align-middle">REPACK</span>
                                  )}
                                  {a.children && a.children.length > 0 && (
                                    <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 align-middle inline-flex items-center gap-0.5">
                                      <Package size={9} />{a.children.length} repack{a.children.length === 1 ? '' : 's'}
                                      {a.has_child_warning && <AlertTriangle size={9} className="text-red-600" />}
                                    </span>
                                  )}
                                </p>
                              </div>
                              <p className="text-[10px] text-slate-400">
                                {a.sku} {a.category && `· ${a.category}`}
                                {a.is_repack && a.parent_name && (
                                  <span className="ml-1 text-purple-500"> from {a.parent_name} ({a.units_per_parent}/parent)</span>
                                )}
                              </p>
                              {a.branch_name && (
                                <Badge variant="outline" className="text-[9px] py-0 px-1.5 mt-0.5">{a.branch_name}</Badge>
                              )}
                              {/* Retail / Wholesale review status chips */}
                              <div className="flex items-center gap-1 mt-1.5 flex-wrap" data-testid={`review-status-${a.id}`}>
                                {reviewChip('Retail',    a.retail_reviewed,    a.retail_last_set,    a.retail_updated_by)}
                                {reviewChip('Wholesale', a.wholesale_reviewed, a.wholesale_last_set, a.wholesale_updated_by)}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right">
                              <span className="text-sm text-slate-500 line-through">{formatPHP(a.old_capital)}</span>
                            </td>
                            <td className="px-3 py-2 text-right">
                              {/* New (current) capital — shows BOTH references; the
                                  active costing basis (used for margin checks) is badged. */}
                              <div className="flex items-baseline justify-end gap-1.5">
                                <span className="text-[9px] uppercase text-slate-400 font-semibold">MA</span>
                                <span className={`text-sm font-bold ${a.method === 'moving_average' ? (a.direction === 'up' ? 'text-red-700' : 'text-emerald-700') : 'text-slate-500'}`}>{formatPHP(a.moving_average)}</span>
                                {a.method === 'moving_average' && (
                                  <span className="text-[8px] font-bold px-1 py-0.5 rounded bg-blue-100 text-blue-700">ACTIVE</span>
                                )}
                              </div>
                              <div className="flex items-baseline justify-end gap-1.5 mt-0.5">
                                <span className="text-[9px] uppercase text-slate-400 font-semibold">LP</span>
                                <span className={`text-sm font-bold ${a.method !== 'moving_average' ? (a.direction === 'up' ? 'text-red-700' : 'text-emerald-700') : 'text-slate-500'}`}>{formatPHP(a.last_purchase || a.po_unit_price)}</span>
                                {a.method !== 'moving_average' && (
                                  <span className="text-[8px] font-bold px-1 py-0.5 rounded bg-slate-200 text-slate-700">ACTIVE</span>
                                )}
                              </div>
                              <p className={`text-[10px] font-semibold mt-1 ${a.direction === 'up' ? 'text-red-600' : 'text-emerald-600'}`}>
                                {a.delta_amount >= 0 ? '+' : ''}{formatPHP(a.delta_amount)}
                                {a.delta_pct !== null && (
                                  <span className="ml-1 opacity-75">({a.delta_pct >= 0 ? '+' : ''}{a.delta_pct.toFixed(1)}%)</span>
                                )}
                              </p>
                            </td>
                            <td className="px-1 bg-slate-100 w-px" />
                            {capPrimary.map(s => renderPriceCell(s, true))}
                            {capOther.map(s => renderPriceCell(s, false))}
                            <td className="px-3 py-2 text-[10px] text-slate-500">
                              <div>{a.source_type === 'purchase_order' ? <>PO <strong>{a.source_ref}</strong></> : a.source_type === 'manual_edit' ? <strong>Manual capital edit</strong> : a.source_type === 'capital_propagation' ? <><strong>Propagated</strong>{a.source_branch_name ? <> from {a.source_branch_name}</> : ''}{a.source_ref && a.source_ref !== 'propagation' ? <> · {a.source_ref}</> : ''}</> : <>Transfer <strong>{a.source_ref}</strong></>}</div>
                              {a.vendor && <div>Vendor: {a.vendor}</div>}
                              {a.from_branch && <div>From: {a.from_branch}</div>}
                              <div className="text-slate-400">{formatDateTime(a.changed_at)}</div>
                              {a.changed_by_name && <div className="text-slate-400">by {a.changed_by_name}</div>}
                            </td>
                            <td className="px-2 py-2">
                              <div className="flex flex-col gap-1">
                                <Button size="sm"
                                  onClick={() => handleSaveCapAlert(a)}
                                  className="h-7 text-[11px] bg-[#1A4D2E] hover:bg-[#14532d] text-white"
                                  data-testid={`cap-update-${a.id}`}>
                                  <Lock size={10} className="mr-1" />
                                  Confirm Prices
                                </Button>
                                {/* Snooze — hides temporarily, resurfaces after expiry */}
                                <div className="relative" ref={snoozeMenuId === a.id ? snoozeMenuRef : null}>
                                  <Button size="sm" variant="outline"
                                    onClick={() => setSnoozeMenuId(v => v === a.id ? null : a.id)}
                                    className="h-7 text-[11px] text-slate-500 gap-0.5"
                                    data-testid={`snooze-cap-btn-${a.id}`}>
                                    <Clock size={9} /> Snooze <ChevronDown size={9} />
                                  </Button>
                                  {snoozeMenuId === a.id && (
                                    <div className="absolute right-0 bottom-8 z-50 bg-white border border-slate-200 rounded-lg shadow-xl w-44 py-1">
                                      <p className="text-[10px] text-slate-400 px-3 py-1 font-medium uppercase">Snooze for…</p>
                                      {CAP_SNOOZE_OPTIONS.map(opt => (
                                        <button key={opt.ms}
                                          onClick={() => snoozeCapAlert(a.id, opt.ms)}
                                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-amber-50 flex items-center gap-2">
                                          <Clock size={10} className="text-amber-500" /> {opt.label}
                                        </button>
                                      ))}
                                      <div className="border-t border-slate-100 mt-1 pt-1">
                                        <button
                                          onClick={() => { setSnoozeMenuId(null); ackCapAlert(a.id); }}
                                          className="w-full text-left px-3 py-1.5 text-[11px] text-slate-400 hover:bg-slate-50"
                                          data-testid={`ack-cap-${a.id}`}>
                                          Accept as-is (don't resurface)
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                          {/* Child repack rows (when expanded). Same column
                              count as parent — uses derived capital from
                              parent_new_cap / units_per_parent. Updating
                              these rides on the parent's single PIN unlock. */}
                          {capExpanded[a.id] && (a.children || []).map(c => {
                            const childMarginByKey = {};
                            (c.scheme_margins || []).forEach(sm => { childMarginByKey[sm.scheme_key] = sm; });
                            const renderChildCell = (s, primary) => {
                              const currentPrice = parseFloat(c.prices?.[s.key] || 0);
                              const editVal = (capChildEditPrices[a.id]?.[c.product_id] || {})[s.key] ?? '';
                              const editNum = parseFloat(editVal);
                              const hasEdit = !isNaN(editNum) && editNum > 0;
                              const willBeBelow = hasEdit && editNum < c.derived_new_capital;
                              const newMargin = hasEdit ? editNum - c.derived_new_capital : null;
                              const newMarginPct = hasEdit && editNum > 0 ? (newMargin / editNum * 100) : null;
                              const isThin = hasEdit && !willBeBelow && (newMargin < 5.0 || (newMarginPct !== null && newMarginPct < 5.0));
                              const sm = childMarginByKey[s.key];
                              return (
                                <td key={s.key} className={`px-2 py-1.5 ${primary ? 'bg-purple-50/30' : 'bg-slate-50/30'}`}>
                                  <div>
                                    <div className={`text-[10px] mb-0.5  ${sm?.is_below_cost ? 'text-red-500 line-through' : sm?.is_thin ? 'text-amber-600' : 'text-slate-400'}`}>
                                      was: {currentPrice > 0 ? formatPHP(currentPrice) : '—'}
                                      {sm?.is_below_cost && <span className="ml-1 text-[9px] font-bold text-red-600">BELOW COST</span>}
                                      {sm?.is_thin && !sm?.is_below_cost && <span className="ml-1 text-[9px] font-bold text-amber-600">THIN</span>}
                                    </div>
                                    <CalcInput value={editVal}
                                      onChange={(v) => updateCapChildPrice(a.id, c.product_id, s.key, v)}
                                      placeholder={primary ? `min ₱${c.derived_new_capital.toFixed(2)}` : 'optional'}
                                      className={`h-7 text-xs text-right  w-full ${
                                        willBeBelow
                                          ? 'border-red-500 bg-red-50 text-red-700 font-bold ring-1 ring-red-400'
                                          : isThin
                                          ? 'border-amber-400 bg-amber-50 text-amber-800'
                                          : hasEdit
                                          ? 'border-emerald-400 bg-emerald-50 text-emerald-800 font-bold'
                                          : 'border-slate-200'
                                      }`}
                                      data-testid={`cap-child-price-${a.id}-${c.product_id}-${s.key}`} />
                                    {willBeBelow && (
                                      <p className="text-[9px] text-red-600 mt-0.5 text-right font-bold flex items-center justify-end gap-1">
                                        <AlertTriangle size={9} /> ₱{(c.derived_new_capital - editNum).toFixed(2)} below derived cap
                                      </p>
                                    )}
                                    {isThin && !willBeBelow && (
                                      <p className="text-[9px] text-amber-700 mt-0.5 text-right font-semibold">
                                        Thin: +{formatPHP(newMargin)} ({newMarginPct?.toFixed(1)}%)
                                      </p>
                                    )}
                                    {hasEdit && !willBeBelow && !isThin && (
                                      <p className="text-[9px] text-emerald-600 mt-0.5 text-right">
                                        +{formatPHP(newMargin)} ({newMarginPct?.toFixed(1)}%)
                                      </p>
                                    )}
                                  </div>
                                </td>
                              );
                            };
                            return (
                              <tr key={`${a.id}-child-${c.product_id}`}
                                  className="border-b border-purple-100/60 bg-purple-50/10 hover:bg-purple-50/30"
                                  data-testid={`cap-child-row-${a.id}-${c.product_id}`}>
                                <td className="px-3 py-1.5 pl-10">
                                  <div className="flex items-center gap-1.5">
                                    <Package size={11} className="text-purple-500 flex-shrink-0" />
                                    <p className="text-xs font-semibold text-purple-900 truncate">
                                      {c.product_name}
                                      <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 align-middle">REPACK</span>
                                    </p>
                                  </div>
                                  <p className="text-[10px] text-slate-400 ml-5">
                                    {c.sku} · {c.units_per_parent}/parent · derived
                                  </p>
                                </td>
                                <td className="px-3 py-1.5 text-right">
                                  <span className="text-xs text-slate-500 line-through">{formatPHP(c.derived_old_capital)}</span>
                                  <p className="text-[9px] text-slate-400">/{c.units_per_parent}</p>
                                </td>
                                <td className="px-3 py-1.5 text-right">
                                  <span className={`text-xs  font-bold ${a.direction === 'up' ? 'text-red-700' : 'text-emerald-700'}`}>
                                    {formatPHP(c.derived_new_capital)}
                                  </span>
                                  <p className={`text-[9px] font-semibold ${a.direction === 'up' ? 'text-red-600' : 'text-emerald-600'}`}>
                                    {c.derived_delta_amount >= 0 ? '+' : ''}{formatPHP(c.derived_delta_amount)}
                                  </p>
                                </td>
                                <td className="px-3 py-1.5 text-right text-[10px] text-slate-400">
                                  —
                                </td>
                                <td className="px-1 bg-slate-100 w-px" />
                                {capPrimary.map(s => renderChildCell(s, true))}
                                {capOther.map(s => renderChildCell(s, false))}
                                <td className="px-3 py-1.5 text-[10px] text-purple-600 italic">
                                  Derived from {a.product_name}
                                </td>
                                <td className="px-2 py-1.5 text-[9px] text-slate-400 italic text-center">
                                  Saves<br/>with parent
                                </td>
                              </tr>
                            );
                          })}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          )}

          {/* Footer */}
          <div className="px-6 py-3 border-t bg-slate-50 flex items-center justify-between">
            <p className="text-xs text-slate-500">
              {tab === 'below_cost' ? (
                <>Scans run every 5 minutes. Notifications sent to owner and branch managers.</>
              ) : (
                <>Capital change alerts persist until retail and wholesale are updated after the cost change. ≥₱1 threshold.</>
              )}
              {skipUntil && skipUntil > Date.now() && tab === 'below_cost' && (
                <span className="ml-2 text-amber-600">
                  Next popup: {formatTime(skipUntil)}
                </span>
              )}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setDialog(false)}>
                Close
              </Button>
              {tab === 'below_cost' && (
                <Button size="sm" onClick={handleSaveAll} disabled={saving || !issues.length}
                  className="bg-[#1A4D2E] hover:bg-[#14532d] text-white">
                  <Lock size={11} className="mr-1.5" />
                  Update All ({issues.length}) — PIN required
                </Button>
              )}
              {tab === 'capital_changes' && visibleCapAlerts.length > 0 && (
                <Button size="sm" onClick={handleSaveAllCap} disabled={saving || !visibleCapAlerts.length}
                  className="bg-[#1A4D2E] hover:bg-[#14532d] text-white"
                  data-testid="confirm-all-cap-footer-btn">
                  <Lock size={11} className="mr-1.5" />
                  Confirm All ({visibleCapAlerts.length}) — PIN required
                </Button>
              )}
              {/* Re-review — shown when there are 0 alerts so admin can resurface
                  previously-dismissed historical changes (last 12 months only) */}
              {tab === 'capital_changes' && visibleCapAlerts.length === 0 && currentBranch?.id && user?.role === 'admin' && (
                <Button size="sm" variant="outline"
                  onClick={async () => {
                    if (!window.confirm(`Re-surface capital changes from the last 12 months for ${currentBranch.name}?\n\nOnly changes where retail/wholesale was NOT updated after the capital change will actually appear — already-reviewed products won't show.`)) return;
                    try {
                      const r = await api.post(`${BACKEND_URL}/api/products/capital-change-alerts/reset-review`, {
                        branch_id: currentBranch.id,
                        months: 12,
                      });
                      toast.success(`${r.data.reset_count} record(s) re-opened — scanning now…`);
                      fetchCapAlerts();
                    } catch { toast.error('Failed to reset'); }
                  }}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="reset-cap-review-btn">
                  <RefreshCw size={11} className="mr-1.5" />
                  Re-review (last 12 months)
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin PIN Modal — gates all price changes from Smart Price Checker */}
      <Dialog open={!!pinModal} onOpenChange={v => { if (!v) { setPinModal(null); setAdminPin(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Lock size={16} className="text-amber-600" />
              Admin Authorization Required
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-600">
              {pinModal?.kind === 'all'
                ? `Apply price updates to ${issues.length} product${issues.length === 1 ? '' : 's'}.`
                : pinModal?.kind === 'capital_change_all'
                ? `Confirm branch prices for ${visibleCapAlerts.length} capital change alert${visibleCapAlerts.length === 1 ? '' : 's'}. Prices will be saved to this branch.`
                : pinModal?.kind === 'capital_change'
                ? `Confirm branch prices for ${pinModal?.alert?.product_name} (capital ${pinModal?.alert?.direction === 'up' ? 'increased' : 'decreased'}). Prices will be saved to this branch.`
                : `Apply price update to ${pinModal?.issue?.product_name}.`}
            </p>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Only an admin PIN or TOTP code can authorize price changes from Smart Price Checker. Manager / cashier PINs will be rejected.
            </p>
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1 block">Admin PIN or TOTP</label>
              <Input
                type="password" autoComplete="one-time-code" data-lpignore="true" inputMode="numeric"
                value={adminPin} onChange={e => setAdminPin(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') executePinned(); }}
                placeholder="Enter PIN or 6-digit TOTP" className="h-9"
                autoFocus data-testid="smart-price-pin-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => { setPinModal(null); setAdminPin(''); }}>
                Cancel
              </Button>
              <Button size="sm" onClick={executePinned} disabled={pinSubmitting || !adminPin}
                className="bg-[#1A4D2E] hover:bg-[#14532d] text-white" data-testid="smart-price-pin-confirm">
                {pinSubmitting ? <RefreshCw size={12} className="animate-spin mr-1.5" /> : <Check size={12} className="mr-1.5" />}
                Authorize &amp; Apply
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Print Prompt — appears after PIN-confirmed price save */}
      <Dialog open={!!printPrompt} onOpenChange={v => { if (!v) setPrintPrompt(null); }}>
        <DialogContent className="max-w-sm" data-testid="print-price-prompt">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Printer size={17} className="text-[#1A4D2E]" />
              Print Price Update List?
            </DialogTitle>
          </DialogHeader>
          {printPrompt && (
            <div className="space-y-4 py-1">
              <p className="text-sm text-slate-600">
                <strong>{printPrompt.items.length} product{printPrompt.items.length !== 1 ? 's' : ''}</strong> {printPrompt.report_type === 'capital_change' ? 'capital changes confirmed' : 'below-cost prices fixed'}.
                Print a dot-matrix reference sheet to update physical price tags/boards at <strong>{currentBranch?.name || 'this branch'}</strong>.
              </p>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => setPrintPrompt(null)}
                  data-testid="skip-print-btn">
                  Skip for now
                </Button>
                <Button size="sm"
                  onClick={() => { triggerPrint(printPrompt); setPrintPrompt(null); }}
                  className="bg-[#1A4D2E] hover:bg-[#14532d] text-white gap-1.5"
                  data-testid="print-price-list-btn">
                  <Printer size={13} /> Print Price List
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
